<template>
  <div v-if="visivel" class="notif card">
    <span class="notif-ponto" :class="estado" aria-hidden="true"></span>

    <span class="notif-txt">
      {{ titulo }}
      <span v-if="dica" class="notif-dica">{{ dica }}</span>
    </span>

    <span v-if="estado === 'desligado'" class="notif-acao">
      <button class="btn-primary" :disabled="ocupado" @click="ativar">
        {{ ocupado ? 'Ativando…' : 'Ativar' }}
      </button>
    </span>
    <button
      v-else-if="estado === 'ligado'"
      class="notif-link"
      :disabled="ocupado"
      @click="desativar"
    >Desativar</button>
  </div>
</template>

<script>
import api from '../../lib/api'

// Base64 url-safe -> Uint8Array, formato que o pushManager exige da chave VAPID.
function chaveParaBytes(base64) {
  const pad = '='.repeat((4 - base64.length % 4) % 4)
  const bruto = window.atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from([...bruto].map(c => c.charCodeAt(0)))
}

function apelidoDoAparelho() {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return 'Android'
  if (/Macintosh/.test(ua)) return 'Mac'
  return 'Navegador'
}

export default {
  name: 'NotificacoesCard',
  data() {
    return { visivel: false, estado: 'desligado', ocupado: false, chavePublica: null }
  },
  computed: {
    /**
     * Uma linha, autoexplicativa sem depender da cor da bolinha — quem nao
     * distingue verde de cinza le "ativas" no texto.
     */
    titulo() {
      return {
        ligado: 'Notificações ativas neste aparelho',
        desligado: 'Notificações neste aparelho',
        instalar: 'Requer instalação na Tela de Início',
        bloqueado: 'Notificações bloqueadas neste aparelho'
      }[this.estado]
    },
    /**
     * So existe quando ha o que fazer. No estado ligado seria texto que se
     * parabeniza: repetiria o que o titulo e a bolinha ja dizem, ocupando
     * metade do card por nada.
     */
    dica() {
      return {
        ligado: null,
        desligado: 'Receba um aviso a cada pagamento confirmado, mesmo com o painel fechado.',
        instalar: 'Toque em Compartilhar → Adicionar à Tela de Início e abra o painel por lá.',
        bloqueado: 'Reative em Ajustes → Notificações antes de tentar de novo.'
      }[this.estado]
    }
  },
  async mounted() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
    try {
      const { data } = await api.get('/push/config')
      if (!data.ligado) return
      this.chavePublica = data.chavePublica
      this.visivel = true
      await this.lerEstado()
    } catch { /* sem push configurado no servidor: o card simplesmente nao aparece */ }
  },
  methods: {
    aviso(message, type) {
      this.$toast.open({ message, type, position: 'top-right' })
    },
    // iOS so expoe notificacao para app aberto pela tela de inicio. Fora dai o
    // botao falharia sem explicacao, entao a instrucao toma o lugar dele.
    ehIosForaDoApp() {
      const ios = /iPhone|iPad|iPod/.test(navigator.userAgent)
      return ios && !window.navigator.standalone
    },
    async lerEstado() {
      if (this.ehIosForaDoApp()) { this.estado = 'instalar'; return }
      if (Notification.permission === 'denied') { this.estado = 'bloqueado'; return }
      const reg = await navigator.serviceWorker.ready
      const assinatura = await reg.pushManager.getSubscription()
      this.estado = assinatura ? 'ligado' : 'desligado'
    },
    async ativar() {
      this.ocupado = true
      try {
        const permissao = await Notification.requestPermission()
        if (permissao !== 'granted') { this.estado = 'bloqueado'; return }
        const reg = await navigator.serviceWorker.ready
        const assinatura = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: chaveParaBytes(this.chavePublica)
        })
        const { endpoint, keys } = assinatura.toJSON()
        await api.post('/push/inscrever', { endpoint, keys, apelido: apelidoDoAparelho() })
        this.estado = 'ligado'
        this.aviso('Notificações ativadas neste aparelho', 'success')
      } catch {
        this.aviso('Não foi possível ativar as notificações', 'error')
      } finally {
        this.ocupado = false
      }
    },
    async desativar() {
      this.ocupado = true
      try {
        const reg = await navigator.serviceWorker.ready
        const assinatura = await reg.pushManager.getSubscription()
        if (assinatura) {
          await api.post('/push/desinscrever', { endpoint: assinatura.endpoint })
          await assinatura.unsubscribe()
        }
        this.estado = 'desligado'
      } catch {
        this.aviso('Não foi possível desativar', 'error')
      } finally {
        this.ocupado = false
      }
    }
  }
}
</script>

<style scoped>
/*
 * Faixa fina, nao bloco. O card antigo empilhava titulo, chip, paragrafo e
 * botao — quatro linhas para dizer "esta ligado".
 */
.notif {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
  padding: 10px var(--spacing-base);
  margin-bottom: var(--spacing-base);
}

.notif-ponto {
  flex: 0 0 auto;
  width: 7px; height: 7px;
  border-radius: 999px;
  background: var(--color-text-muted);
}
.notif-ponto.ligado { background: #16a34a; }
.notif-ponto.instalar,
.notif-ponto.bloqueado { background: var(--color-danger); }

.notif-txt {
  flex: 1 1 220px;
  min-width: 0;
  font-size: var(--text-sm);
  line-height: 1.4;
  color: var(--color-text-primary);
}
.notif-dica {
  display: block;
  margin-top: 2px;
  font-size: var(--text-xs);
  line-height: 1.45;
  color: var(--color-text-muted);
}

.notif-acao { flex: 0 0 auto; }

/* Acao rara e destrutiva do proprio recurso: nao deve competir com nada. */
.notif-link {
  flex: 0 0 auto;
  background: none; border: none; padding: 4px;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  text-decoration: underline;
  cursor: pointer;
}
.notif-link:disabled { opacity: 0.6; cursor: default; }

@media (max-width: 520px) {
  .notif { padding: 10px 12px; }
  /* Só a ação principal ganha a linha inteira. Dar largura total ao
     "Desativar" convidaria ao toque acidental. */
  .notif-acao { flex: 1 1 100%; }
  .notif-acao .btn-primary { width: 100%; }
}
</style>
